/**
 * CihFrameworkCards — 2-column grid of substantial framework cards. Each card
 * shows a colored type badge, the framework name, description, and a chevron
 * to the framework detail page. Contained in the standard page container and
 * rendered as soft rounded cards with the brand shadow ramp.
 *
 * Aliases: framework cards, framework grid, framework list
 */

import { Link } from "@tanstack/react-router";

import { FRAMEWORK_TYPE_META } from "~/components/cih/constants";
import { CihSectionHeader } from "~/components/cih/section-header";
import { NamedIcon } from "~/components/ui/named-icon";
import type { FrameworkType } from "~/lib/schemas/db-enums-cih";

type FrameworkCardData = {
  readonly id: string;
  readonly slug: string | null;
  readonly name: string;
  readonly type: string;
  readonly description?: string | null;
  readonly coaches?: number;
  readonly ctps?: number;
  readonly challenges?: number;
};

type Props = {
  readonly frameworks: ReadonlyArray<FrameworkCardData>;
  readonly sectionLabel: string;
  readonly heading: string;
};

export function CihFrameworkCards({
  frameworks,
  sectionLabel,
  heading,
}: Props) {
  const withSlug = frameworks.filter(
    (fw): fw is FrameworkCardData & { slug: string } => fw.slug != null,
  );
  if (withSlug.length === 0) return null;

  return (
    <>
      <CihSectionHeader label={sectionLabel} title={heading} />
      <div className="mx-auto max-w-7xl px-4 pb-14 md:pb-20 xl:px-0">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-5">
          {withSlug.map((fw) => {
            const meta =
              FRAMEWORK_TYPE_META[fw.type as FrameworkType] ??
              FRAMEWORK_TYPE_META.framework;

            return (
              <Link
                key={fw.id}
                to="/frameworks/$slug"
                params={{ slug: fw.slug }}
                className="group flex flex-col gap-3 rounded-2xl border border-gray-100 bg-white p-7 shadow-xs transition duration-300 hover:-translate-y-0.5 hover:border-gray-200 hover:shadow-md md:p-8"
              >
                <div
                  className={`inline-flex w-fit items-center gap-1.5 text-[10px] font-medium tracking-[0.22em] uppercase ${meta.text}`}
                >
                  <NamedIcon name={meta.icon} className="h-3 w-3" />
                  {meta.label}
                </div>
                <h3 className="font-heading text-2xl font-medium tracking-tight text-gray-900">
                  {fw.name}
                </h3>
                {fw.description ? (
                  <p className="text-sm leading-relaxed text-gray-500">
                    {fw.description}
                  </p>
                ) : null}
                {(fw.ctps ?? 0) > 0 || (fw.challenges ?? 0) > 0 ? (
                  <p className="text-xs font-medium tracking-wider text-gray-500 uppercase">
                    {[
                      (fw.ctps ?? 0) > 0
                        ? `${fw.ctps} ${fw.ctps === 1 ? "program" : "programs"}`
                        : null,
                      (fw.challenges ?? 0) > 0
                        ? `${fw.challenges} ${fw.challenges === 1 ? "challenge" : "challenges"}`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </p>
                ) : null}
                <span className="mt-auto pt-3 text-sm font-medium text-gray-900">
                  Read{" "}
                  <span className="inline-block transition-transform duration-300 group-hover:translate-x-0.5">
                    →
                  </span>
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </>
  );
}
